import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { X, Download, CheckSquare, Square } from "lucide-react";
import { toast } from "react-toastify";
import { getAccessToken } from "../../../../../../services/utils/app.data";
import ENDPOINTS from "../../../../../../services/utils/endpoints";
import { JobTitleOrderBy } from "../../../../../../models/job-titles.dto";

interface ExportJobTitlesModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	companyId: number;
	currentOrderBy?: JobTitleOrderBy;
}

interface ExportColumn {
	key: string;
	label: string;
	selected: boolean;
}

// Keys must match ExportJobTitleQueryDto exactly.
// The previously included fields (companyLevelCode, companyLevelName,
// employeeBandCode, employeeBandName, active, disabilityQuota) are not
// in the DTO and were causing the backend "not allowed" validation errors.
const EXPORT_COLUMNS: ExportColumn[] = [
	{ key: "jobTitleCode", label: "Job Title Code", selected: true },
	{ key: "jobTitleName", label: "Job Title Name", selected: true },
	{ key: "description", label: "Description", selected: true },
	{ key: "companyLevelId", label: "Company Level", selected: true },
	{ key: "employeeBandId", label: "Employee Band", selected: true },
	{ key: "minimumAge", label: "Minimum Age", selected: true },
	{ key: "maximumAge", label: "Maximum Age", selected: true },
	{
		key: "minimumExperienceYears",
		label: "Minimum Experience Years",
		selected: true,
	},
	{ key: "acceptDisability", label: "Accept Disability", selected: true },
];

const ExportJobTitlesModal: React.FC<ExportJobTitlesModalProps> = ({
	open,
	setOpen,
	companyId,
	currentOrderBy = JobTitleOrderBy.CREATED_AT_ASC,
}) => {
	const [columns, setColumns] = useState<ExportColumn[]>(EXPORT_COLUMNS);
	const [exporting, setExporting] = useState(false);

	const handleClose = () => {
		setOpen(false);
	};

	const toggleColumn = (key: string) => {
		setColumns((prev) =>
			prev.map((col) =>
				col.key === key ? { ...col, selected: !col.selected } : col,
			),
		);
	};

	const toggleSelectAll = () => {
		const allSelected = columns.every((col) => col.selected);
		setColumns((prev) =>
			prev.map((col) => ({ ...col, selected: !allSelected })),
		);
	};

	const handleExport = async () => {
		try {
			const selectedColumns = columns.filter((col) => col.selected);

			if (selectedColumns.length === 0) {
				toast.error("Please select at least one column to export.");
				return;
			}

			setExporting(true);

			// Build query parameters
			const queryParams = new URLSearchParams();
			selectedColumns.forEach((col) => {
				queryParams.append(col.key, "true");
			});
			queryParams.append("orderBy", currentOrderBy);

			const exportUrl = `${ENDPOINTS.PROCESSING_COMPANY.EXPORTS.JOBTITLE_EXPORT.replace(
				"{companyId}",
				companyId.toString(),
			)}?${queryParams.toString()}`;

			const response = await fetch(exportUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${getAccessToken()}`,
				},
			});

			if (!response.ok) {
				toast.error("Job Titles export failed. Please try again.");
				return;
			}

			const contentDisposition = response.headers.get("Content-Disposition");
			const filename =
				contentDisposition?.split("filename=")[1] || "job_titles_export.xlsx";

			const blob = await response.blob();

			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(blob);
			link.download = filename;
			link.click();
			window.URL.revokeObjectURL(link.href);

			toast.success("Job Titles export completed successfully.");
			handleClose();
		} catch (error) {
			console.error(error);
			toast.error("Unable to download job titles.");
		} finally {
			setExporting(false);
		}
	};

	const selectedCount = columns.filter((col) => col.selected).length;
	const allSelected = columns.every((col) => col.selected);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				className: "rounded-2xl shadow-2xl",
			}}
		>
			<DialogTitle className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-xl font-semibold text-gray-800">
							Export Job Titles
						</h2>
						<p className="text-sm text-gray-500 mt-1">
							Select the columns you want to include in your export
						</p>
					</div>
					<IconButton
						onClick={handleClose}
						size="small"
						className="text-gray-400 hover:text-gray-600"
					>
						<X className="w-5 h-5" />
					</IconButton>
				</div>
			</DialogTitle>

			<DialogContent className="py-6 px-6">
				{/* Select All Option */}
				<div className="mb-6 pb-4 border-b border-gray-200">
					<button
						onClick={toggleSelectAll}
						className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
					>
						{allSelected ? (
							<CheckSquare className="w-5 h-5 text-blue-600 flex-shrink-0" />
						) : (
							<Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
						)}
						<span className="font-medium text-gray-700">
							{allSelected ? "Deselect All" : "Select All"}
						</span>
						<span className="ml-auto text-sm text-gray-500">
							{selectedCount} of {columns.length} selected
						</span>
					</button>
				</div>

				{/* Column Selection Grid */}
				<div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
					{columns.map((column) => (
						<button
							key={column.key}
							onClick={() => toggleColumn(column.key)}
							className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
								column.selected
									? "border-blue-200 bg-blue-50 hover:bg-blue-100"
									: "border-gray-200 bg-white hover:bg-gray-50"
							}`}
						>
							{column.selected ? (
								<CheckSquare className="w-5 h-5 text-blue-600 flex-shrink-0" />
							) : (
								<Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
							)}
							<span
								className={`text-sm font-medium ${
									column.selected ? "text-gray-800" : "text-gray-600"
								}`}
							>
								{column.label}
							</span>
						</button>
					))}
				</div>

				{/* Action Buttons */}
				<div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
					<button
						onClick={handleClose}
						disabled={exporting}
						className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					>
						Cancel
					</button>
					<button
						onClick={handleExport}
						disabled={exporting || selectedCount === 0}
						className="px-6 py-2.5 text-sm font-medium text-white bg-primary-mango rounded-lg hover:bg-primary-mango disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
					>
						{exporting ? (
							<>
								<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
								<span>Exporting...</span>
							</>
						) : (
							<>
								<Download className="w-4 h-4" />
								<span>Export {selectedCount} Columns</span>
							</>
						)}
					</button>
				</div>
			</DialogContent>

			<style>{`
				.custom-scrollbar::-webkit-scrollbar {
					width: 6px;
				}
				.custom-scrollbar::-webkit-scrollbar-track {
					background: #f1f5f9;
					border-radius: 3px;
				}
				.custom-scrollbar::-webkit-scrollbar-thumb {
					background: #cbd5e1;
					border-radius: 3px;
				}
				.custom-scrollbar::-webkit-scrollbar-thumb:hover {
					background: #94a3b8;
				}
			`}</style>
		</Dialog>
	);
};

export default ExportJobTitlesModal;
